const VALID_SIDES = new Set(["top", "right", "bottom", "left"]);
const VALID_ALIGNS = new Set(["start", "center", "end"]);

function getSafePlacement(placement, align = "start") {
  if (typeof placement === "string") {
    const [sideCandidate, alignCandidate] = placement.split("-");
    const side = VALID_SIDES.has(sideCandidate) ? sideCandidate : null;
    const crossAlign = VALID_ALIGNS.has(alignCandidate) ? alignCandidate : null;

    if (side) {
      return `${side}-${crossAlign || "start"}`;
    }
  }

  const fallbackAlign = VALID_ALIGNS.has(align) ? align : "start";
  return `bottom-${fallbackAlign}`;
}

function parsePlacement(placement, align) {
  const resolvedPlacement = getSafePlacement(placement, align);
  const [side, crossAlign] = resolvedPlacement.split("-");

  return {
    placement: resolvedPlacement,
    side,
    align: crossAlign || "start",
  };
}

function getOppositeSide(side) {
  switch (side) {
    case "top":
      return "bottom";
    case "bottom":
      return "top";
    case "left":
      return "right";
    case "right":
      return "left";
    default:
      return "bottom";
  }
}

function getPerpendicularSides(side) {
  if (side === "top" || side === "bottom") {
    return ["right", "left"];
  }

  return ["bottom", "top"];
}

function buildPlacementCandidates(placement, flip) {
  const { side, align } = parsePlacement(placement);
  const candidates = [`${side}-${align}`];

  if (!flip) {
    return candidates;
  }

  const fallbackSides = [
    getOppositeSide(side),
    ...getPerpendicularSides(side),
  ];

  fallbackSides.forEach((candidateSide) => {
    candidates.push(`${candidateSide}-${align}`);
  });

  return Array.from(new Set(candidates));
}

function resolveOffsetByPlacement(side, offset = { x: 0, y: 6 }) {
  const offsetX = offset?.x ?? 0;
  const offsetY = offset?.y ?? 0;
  const mainAxisOffset = side === "top" || side === "bottom"
    ? offsetY
    : (offsetX || offsetY);
  const crossAxisOffset = side === "top" || side === "bottom"
    ? offsetX
    : offsetY;

  return {
    mainAxisOffset,
    crossAxisOffset,
  };
}

function resolveCandidateCoords({ anchor, width, height, placement, offset }) {
  const { side, align } = parsePlacement(placement);
  const { mainAxisOffset, crossAxisOffset } = resolveOffsetByPlacement(side, offset);
  const anchorTop = anchor?.top ?? 0;
  const anchorLeft = anchor?.left ?? 0;
  const anchorWidth = anchor?.width ?? 0;
  const anchorHeight = anchor?.height ?? 0;

  if (side === "top" || side === "bottom") {
    let left = anchorLeft + crossAxisOffset;
    if (align === "center") {
      left = anchorLeft + anchorWidth / 2 - width / 2 + crossAxisOffset;
    } else if (align === "end") {
      left = anchorLeft + anchorWidth - width + crossAxisOffset;
    }

    const top = side === "bottom"
      ? anchorTop + anchorHeight + mainAxisOffset
      : anchorTop - height - mainAxisOffset;

    return { top, left };
  }

  let top = anchorTop + crossAxisOffset;
  if (align === "center") {
    top = anchorTop + anchorHeight / 2 - height / 2 + crossAxisOffset;
  } else if (align === "end") {
    top = anchorTop + anchorHeight - height + crossAxisOffset;
  }

  const left = side === "right"
    ? anchorLeft + anchorWidth + mainAxisOffset
    : anchorLeft - width - mainAxisOffset;

  return { top, left };
}

function getOverflowScore({ top, left, width, height, viewportWidth, viewportHeight, gap }) {
  const overflowLeft = Math.max(0, gap - left);
  const overflowTop = Math.max(0, gap - top);
  const overflowRight = Math.max(0, left + width - (viewportWidth - gap));
  const overflowBottom = Math.max(0, top + height - (viewportHeight - gap));

  return {
    total: overflowLeft + overflowTop + overflowRight + overflowBottom,
    overflowLeft,
    overflowTop,
    overflowRight,
    overflowBottom,
  };
}

function clampToViewport({ top, left, width, height, viewportWidth, viewportHeight, gap }) {
  const clamp = (value, min, max) => Math.min(Math.max(value, min), max);
  const maxLeft = Math.max(viewportWidth - width - gap, gap);
  const maxTop = Math.max(viewportHeight - height - gap, gap);

  return {
    left: clamp(left, gap, maxLeft),
    top: clamp(top, gap, maxTop),
  };
}

export function calculateFloatingPosition({
  container,
  dropdown,
  anchor,
  gap = 8,
  offset = { x: 0, y: 6 },
  align = "start",
  placement,
  flip = true,
  strategy = "absolute",
  dropdownSize,
}) {
  if (!container || !dropdown) {
    return {
      top: anchor?.top ?? 0,
      left: anchor?.left ?? 0,
    };
  }

  if (typeof window === "undefined") {
    return {
      top: anchor?.top ?? 0,
      left: anchor?.left ?? 0,
    };
  }

  const containerRect = container.getBoundingClientRect();
  const dropdownRect = dropdown.getBoundingClientRect();

  const viewportWidth = window.innerWidth || document.documentElement?.clientWidth || 0;
  const viewportHeight = window.innerHeight || document.documentElement?.clientHeight || 0;

  const scrollTop = container.scrollTop || 0;
  const scrollLeft = container.scrollLeft || 0;

  const anchorTop = anchor?.top ?? 0;
  const anchorLeft = anchor?.left ?? 0;
  const anchorWidth = anchor?.width ?? 0;
  const anchorHeight = anchor?.height ?? 0;

  const width = dropdownSize?.width ?? dropdownRect.width ?? dropdown.offsetWidth ?? 0;
  const height = dropdownSize?.height ?? dropdownRect.height ?? dropdown.offsetHeight ?? 0;
  const safeGap = typeof gap === "number" ? Math.max(gap, 0) : 8;
  const resolvedPlacement = getSafePlacement(placement, align);

  const anchorInViewport = strategy === "fixed"
    ? {
      top: anchorTop,
      left: anchorLeft,
      width: anchorWidth,
      height: anchorHeight,
    }
    : {
      top: containerRect.top + anchorTop - scrollTop,
      left: containerRect.left + anchorLeft - scrollLeft,
      width: anchorWidth,
      height: anchorHeight,
    };

  const candidates = buildPlacementCandidates(resolvedPlacement, flip);
  let chosenPlacement = candidates[0];
  let chosenCoords = resolveCandidateCoords({
    anchor: anchorInViewport,
    width,
    height,
    placement: chosenPlacement,
    offset,
  });
  let chosenOverflow = getOverflowScore({
    ...chosenCoords,
    width,
    height,
    viewportWidth,
    viewportHeight,
    gap: safeGap,
  });

  for (const candidatePlacement of candidates) {
    const candidateCoords = resolveCandidateCoords({
      anchor: anchorInViewport,
      width,
      height,
      placement: candidatePlacement,
      offset,
    });
    const candidateOverflow = getOverflowScore({
      ...candidateCoords,
      width,
      height,
      viewportWidth,
      viewportHeight,
      gap: safeGap,
    });

    if (candidateOverflow.total === 0) {
      chosenPlacement = candidatePlacement;
      chosenCoords = candidateCoords;
      chosenOverflow = candidateOverflow;
      break;
    }

    if (candidateOverflow.total < chosenOverflow.total) {
      chosenPlacement = candidatePlacement;
      chosenCoords = candidateCoords;
      chosenOverflow = candidateOverflow;
    }
  }

  const clampedViewportCoords = clampToViewport({
    ...chosenCoords,
    width,
    height,
    viewportWidth,
    viewportHeight,
    gap: safeGap,
  });

  if (strategy === "fixed") {
    return {
      top: Number.isFinite(clampedViewportCoords.top) ? clampedViewportCoords.top : 0,
      left: Number.isFinite(clampedViewportCoords.left) ? clampedViewportCoords.left : 0,
      placement: chosenPlacement,
    };
  }

  const left = clampedViewportCoords.left - containerRect.left + scrollLeft;
  const top = clampedViewportCoords.top - containerRect.top + scrollTop;

  return {
    top: Number.isFinite(top) ? top : 0,
    left: Number.isFinite(left) ? left : 0,
    placement: chosenPlacement,
  };
}

export default calculateFloatingPosition;
